import { useId, useState } from 'react';
import type { Content } from '@neopolis/engine';

import { T } from '../textes-interface.js';
import type { ClasseCreee, ClientProf } from './api-prof.js';
import { AnalyseDeSeance } from './AnalyseDeSeance.js';
import { ModeProjection } from './ModeProjection.js';
import { PlancheDeCodes } from './PlancheDeCodes.js';
import { SuiviDeClasse } from './SuiviDeClasse.js';

type Vue = 'accueil' | 'planche' | 'suivi' | 'projection' | 'analyse';

/**
 * L'espace enseignant.
 *
 * Quatre vues : créer une classe, imprimer ses codes, suivre la séance,
 * projeter les solutions pour le débat final.
 *
 * Le PIN n'est gardé qu'en mémoire, le temps de la séance. Rien n'est écrit sur
 * la machine de l'enseignant : une salle de classe est un poste partagé.
 */
export function EcranProf({
  content,
  client,
  onRetourJeu,
}: {
  content: Content;
  client: ClientProf;
  onRetourJeu: () => void;
}) {
  const [vue, setVue] = useState<Vue>('accueil');
  const [classe, setClasse] = useState<ClasseCreee | null>(null);
  const [classCode, setClassCode] = useState('');
  const [pin, setPin] = useState('');
  const [nom, setNom] = useState('');
  const [ilots, setIlots] = useState(8);
  const [erreur, setErreur] = useState<string | null>(null);

  const idNom = useId();
  const idPin = useId();
  const idIlots = useId();
  const idCodeClasse = useId();
  const idPinOuverture = useId();
  const [pinOuverture, setPinOuverture] = useState('');

  if (vue === 'projection') {
    return (
      <ModeProjection
        content={content}
        client={client}
        classCode={classCode}
        pin={pin || pinOuverture}
        onQuitter={() => setVue('suivi')}
      />
    );
  }

  return (
    <main className="mx-auto max-w-5xl space-y-6 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <h1 className="text-3xl font-bold text-slate-900">{T.prof.titre}</h1>
        <button
          type="button"
          onClick={onRetourJeu}
          className="min-h-touch rounded-xl bg-white px-4 font-semibold text-slate-700 shadow-sm"
        >
          {T.prof.retourJeu}
        </button>
      </header>

      {erreur !== null && (
        <p role="alert" className="rounded-xl bg-rose-50 p-3 font-medium text-rose-800">
          {erreur}
        </p>
      )}

      {vue === 'accueil' && (
        <div className="grid gap-6 lg:grid-cols-2">
          <form
            className="space-y-3 rounded-2xl border-2 border-slate-200 bg-white p-5"
            onSubmit={async (evenement) => {
              evenement.preventDefault();
              setErreur(null);
              const creee = await client.creerClasse(nom.trim(), pin, ilots);
              if (!creee) {
                setErreur(T.prof.creationImpossible);
                return;
              }
              setClasse(creee);
              setClassCode(creee.classCode);
              setVue('planche');
            }}
          >
            <h2 className="text-xl font-bold text-slate-900">{T.prof.creerClasse}</h2>

            <div>
              <label htmlFor={idNom} className="block font-semibold text-slate-800">
                {T.prof.nomClasse}
              </label>
              <input
                id={idNom}
                value={nom}
                onChange={(evenement) => setNom(evenement.target.value)}
                maxLength={40}
                className="mt-1 min-h-touch w-full rounded-xl border-2 border-slate-300 px-3 focus:border-sky-600"
              />
              <p className="mt-1 text-sm text-slate-500">{T.prof.nomClasseAide}</p>
            </div>

            <div>
              <label htmlFor={idPin} className="block font-semibold text-slate-800">
                {T.prof.pin}
              </label>
              <input
                id={idPin}
                value={pin}
                onChange={(evenement) => setPin(evenement.target.value.replace(/\D/g, ''))}
                inputMode="numeric"
                maxLength={6}
                className="mt-1 min-h-touch w-full rounded-xl border-2 border-slate-300 px-3 font-mono tracking-widest focus:border-sky-600"
              />
              <p className="mt-1 text-sm text-slate-500">{T.prof.pinAide}</p>
            </div>

            <div>
              <label htmlFor={idIlots} className="block font-semibold text-slate-800">
                {T.prof.nombreIlots}
              </label>
              <input
                id={idIlots}
                type="number"
                min={1}
                max={20}
                value={ilots}
                onChange={(evenement) => setIlots(Number(evenement.target.value))}
                className="mt-1 min-h-touch w-24 rounded-xl border-2 border-slate-300 px-3 focus:border-sky-600"
              />
              <p className="mt-1 text-sm text-slate-500">{T.prof.nombreIlotsAide}</p>
            </div>

            <button
              type="submit"
              disabled={nom.trim() === '' || pin.length < 4}
              className="min-h-touch w-full rounded-xl bg-sky-600 px-6 font-semibold text-white disabled:bg-slate-300 disabled:text-slate-500"
            >
              {T.prof.creer}
            </button>
          </form>

          <form
            className="space-y-3 rounded-2xl border-2 border-slate-200 bg-white p-5"
            onSubmit={async (evenement) => {
              evenement.preventDefault();
              setErreur(null);
              const suivi = await client.suivre(classCode.trim().toUpperCase(), pinOuverture);
              if (!suivi) {
                setErreur(T.prof.ouvertureImpossible);
                return;
              }
              setClassCode(suivi.classCode);
              setVue('suivi');
            }}
          >
            <h2 className="text-xl font-bold text-slate-900">{T.prof.ouvrirClasse}</h2>

            <div>
              <label htmlFor={idCodeClasse} className="block font-semibold text-slate-800">
                {T.prof.codeClasse}
              </label>
              <input
                id={idCodeClasse}
                value={classCode}
                onChange={(evenement) => setClassCode(evenement.target.value)}
                maxLength={6}
                className="mt-1 min-h-touch w-full rounded-xl border-2 border-slate-300 px-3 text-center font-mono text-xl uppercase tracking-widest focus:border-sky-600"
              />
            </div>

            <div>
              <label htmlFor={idPinOuverture} className="block font-semibold text-slate-800">
                {T.prof.pin}
              </label>
              <input
                id={idPinOuverture}
                value={pinOuverture}
                onChange={(evenement) => setPinOuverture(evenement.target.value.replace(/\D/g, ''))}
                inputMode="numeric"
                maxLength={6}
                className="mt-1 min-h-touch w-full rounded-xl border-2 border-slate-300 px-3 font-mono tracking-widest focus:border-sky-600"
              />
            </div>

            <button
              type="submit"
              disabled={classCode.trim().length !== 6 || pinOuverture.length < 4}
              className="min-h-touch w-full rounded-xl bg-white px-6 font-semibold text-sky-700 ring-2 ring-sky-600 disabled:text-slate-400 disabled:ring-slate-300"
            >
              {T.prof.ouvrir}
            </button>
          </form>
        </div>
      )}

      {vue === 'planche' && classe && (
        <PlancheDeCodes classe={classe} pin={pin} onSuivi={() => setVue('suivi')} />
      )}

      {vue === 'suivi' && (
        <SuiviDeClasse
          content={content}
          client={client}
          classCode={classCode}
          pin={pin || pinOuverture}
          onProjection={() => setVue('projection')}
          onAnalyse={() => setVue('analyse')}
          onClasseSupprimee={() => {
            // La classe n'existe plus : rester sur son suivi afficherait un
            // tableau vide qui ne se remplira jamais. On revient à l'accueil,
            // avec un mot pour dire ce qui vient de se passer.
            setClasse(null);
            setClassCode('');
            setPin('');
            setPinOuverture('');
            setErreur(T.prof.classeSupprimee);
            setVue('accueil');
          }}
        />
      )}

      {vue === 'analyse' && (
        <AnalyseDeSeance
          content={content}
          client={client}
          classCode={classCode}
          pin={pin || pinOuverture}
          onQuitter={() => setVue('suivi')}
        />
      )}
    </main>
  );
}
